import React from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Search, Layers, Zap } from 'lucide-react';

/**
 * 3.45.0 — HeadlessSection
 *
 * Marketing block for the v3.44.6 Auto-Crawl + headless WordPress support.
 * Placed in App.js between DifferentiatorsSection and FeaturesSection so the
 * /#headless anchor in Header.jsx scrolls into it.
 *
 * Styling mirrors DifferentiatorsSection (same eyebrow → title-with-gradient
 * → subtitle → grid layout) to feel native to the page rather than bolted on.
 * Comparison table is the unique differentiator vs the other sections.
 */
const HeadlessSection = () => {
  const { t } = useTranslation();

  const features = [
    { icon: Search,  id: 'autoCrawl',     iconBg: 'from-[#0055FF] to-[#10B981]' },
    { icon: Globe,   id: 'renderedHtml',  iconBg: 'from-[#10B981] to-[#00E5FF]' },
    { icon: Layers,  id: 'fourModes',     iconBg: 'from-[#8B5CF6] to-[#0055FF]' },
    { icon: Zap,     id: 'zeroSetup',     iconBg: 'from-[#F59E0B] to-[#EC4899]' },
  ];

  const compRows = [
    { label: t('headless.comparison.row1'), us: true,  yoast: false, rm: false, aioseo: false },
    { label: t('headless.comparison.row2'), us: true,  yoast: false, rm: false, aioseo: false },
    { label: t('headless.comparison.row3'), us: true,  yoast: false, rm: false, aioseo: false },
    { label: t('headless.comparison.row4'), us: true,  yoast: 'partial', rm: 'partial', aioseo: 'partial' },
    { label: t('headless.comparison.row5'), us: t('headless.comparison.usSetupTime'), yoast: 'N/A', rm: 'N/A', aioseo: 'N/A' },
  ];

  const cell = (v) => {
    if (v === true) return <span className="text-[#10B981] font-semibold text-lg">✓</span>;
    if (v === false) return <span className="text-[#A1A1AA]">—</span>;
    if (v === 'partial') return <span className="text-[#F59E0B] text-xs">{t('headless.comparison.wpOnly')}</span>;
    return <span className="text-white">{v}</span>;
  };

  return (
    <section
      id="headless"
      className="py-24 px-6 bg-[#0a0a0a] border-t border-white/5"
      data-testid="headless-section"
    >
      <div className="max-w-7xl mx-auto">
        {/* Eyebrow + Title + Subtitle (matches DifferentiatorsSection visual pattern) */}
        <div className="text-center mb-16 animate-fade-up">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#10B981] font-semibold">
            <span className="px-2 py-0.5 rounded-full bg-gradient-to-r from-[#10B981] to-[#00E5FF] text-black text-[10px]">
              {t('headless.badge')}
            </span>
            {t('headless.eyebrow')}
          </span>
          <h2 className="font-['Outfit'] text-4xl sm:text-5xl font-medium tracking-tight mt-4 mb-6">
            {t('headless.title1')}
            <span className="text-gradient">{t('headless.title2')}</span>
            {t('headless.title3')}
          </h2>
          <p className="text-lg text-[#A1A1AA] max-w-2xl mx-auto">
            {t('headless.subtitle')}
          </p>
        </div>

        {/* Features grid (4 cards) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {features.map((f) => {
            const Icon = f.icon;
            return (
              <div
                key={f.id}
                className="rounded-2xl bg-white/[0.02] border border-white/5 p-6 hover:bg-white/[0.04] transition-colors"
                data-testid={`headless-feature-${f.id}`}
              >
                <div className={`inline-flex w-12 h-12 rounded-xl bg-gradient-to-br ${f.iconBg} items-center justify-center mb-4`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-['Outfit'] text-xl font-semibold mb-2">
                  {t(`headless.cards.${f.id}.title`)}
                </h3>
                <p className="text-sm text-[#A1A1AA] leading-relaxed">
                  {t(`headless.cards.${f.id}.desc`)}
                </p>
              </div>
            );
          })}
        </div>

        {/* Comparison table */}
        <div className="rounded-2xl bg-white/[0.02] border border-white/5 overflow-hidden">
          <div className="px-6 py-4 border-b border-white/5">
            <h3 className="font-['Outfit'] text-xl font-semibold">
              {t('headless.comparison.title')}
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-white/[0.03] text-[#A1A1AA] text-xs uppercase tracking-wider">
                  <th className="px-6 py-3 text-left font-medium"></th>
                  <th className="px-4 py-3 font-medium text-[#0055FF]">AEO Orchestra</th>
                  <th className="px-4 py-3 font-medium">Yoast</th>
                  <th className="px-4 py-3 font-medium">RankMath</th>
                  <th className="px-4 py-3 font-medium">AIOSEO</th>
                </tr>
              </thead>
              <tbody>
                {compRows.map((row, idx) => (
                  <tr key={idx} className="border-t border-white/5">
                    <td className="px-6 py-3 text-left text-white font-medium">{row.label}</td>
                    <td className="px-4 py-3 text-center">{cell(row.us)}</td>
                    <td className="px-4 py-3 text-center">{cell(row.yoast)}</td>
                    <td className="px-4 py-3 text-center">{cell(row.rm)}</td>
                    <td className="px-4 py-3 text-center">{cell(row.aioseo)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer link to deep-dive page (existing /headless-wordpress/ standalone) */}
        <div className="text-center mt-8">
          <a
            href="/headless-wordpress/"
            className="inline-flex items-center gap-2 text-[#0055FF] hover:text-[#3377FF] transition-colors text-sm font-medium"
            data-testid="headless-deep-dive-link"
          >
            {t('headless.deepDive')} →
          </a>
        </div>
      </div>
    </section>
  );
};

export default HeadlessSection;
